import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, Stack, SxProps, Typography } from '@mui/material';
import { Campaign, CampaignStatus } from 'services/campaign/types';
import { ToastType, useToast } from 'services/toast';
import { useDrawerV2 } from 'common/drawer/hooks/useDrawerV2';
import { Fields, Form, FormRef } from 'common/form';
import { useStopCampaignMutation } from 'generated-graphql/hooks/apollo-hooks';
import { getCampaignCreationTemplateVariables } from 'pages/CampaignCreation/utils/getCampaignCreationTemplateVariables';
import { capitalizeFirstLetter } from 'utils/fomatting';
import * as Yup from 'yup';

const bulletListSx: SxProps = {
  margin: '8px 0 0 0',
  paddingLeft: '15px',
  fontSize: '16px',
  lineHeight: '26px',
  listStyleType: '"•   "'
};

const stopButtonSx: SxProps = {
  backgroundColor: 'error.main',
  '&:hover': { backgroundColor: 'error.dark' }
};

const CANCELLATION_MESSAGE_SUB_LABEL =
  'To personalise for each recipient, type # and choose a variable.';

const stopCampaignFormSchema = Yup.object({
  sendCancellationMessage: Yup.string().required(),
  cancellationMessage: Yup.string().when('sendCancellationMessage', {
    is: 'yes',
    then: (schema) => schema.required('Write a message.'),
    otherwise: (schema) => schema.optional()
  })
});

type StopCampaignFormData = Yup.InferType<typeof stopCampaignFormSchema>;

type Props = {
  campaign: Campaign;
  onCompleted?: (campaign: Campaign) => void;
};

export const StopCampaignDrawer: React.FC<Props> = ({
  campaign,
  onCompleted
}) => {
  const { closeDrawer } = useDrawerV2();
  const { showToast } = useToast();
  const formRef = useRef<FormRef<StopCampaignFormData>>(null);
  const [sendMessage, setSendMessage] = useState(false);

  useEffect(() => {
    const subscription = formRef.current?.watch(
      (data: StopCampaignFormData) => {
        setSendMessage(data.sendCancellationMessage === 'yes');
      }
    );
    return () => subscription?.unsubscribe?.();
  }, []);

  const availableVariables = useMemo(
    () =>
      getCampaignCreationTemplateVariables(campaign.type).map((variable) => ({
        label: capitalizeFirstLetter(
          variable.toLowerCase().replaceAll('_', ' ')
        ),
        value: variable
      })),
    [campaign.type]
  );

  const stopCampaignFields: Fields<StopCampaignFormData> = {
    sendCancellationMessage: {
      type: 'radio',
      label: 'Send cancellation message to candidates?',
      options: [
        { label: 'No', value: 'no' },
        { label: 'Yes', value: 'yes' }
      ]
    },
    cancellationMessage: {
      type: 'templated-text',
      label: 'Write message',
      subLabel: CANCELLATION_MESSAGE_SUB_LABEL,
      availableVariables,
      minHeight: 187,
      hidden: !sendMessage
    }
  };

  const [stopCampaign, { loading }] = useStopCampaignMutation({
    onCompleted: (data) => {
      showToast({
        type: ToastType.Success,
        title: 'Campaign stopped and archived',
        ...(sendMessage && {
          message: 'Cancellation messages have been sent.'
        })
      });
      if (onCompleted && data?.stopCampaign?.campaign) {
        onCompleted({
          ...campaign,
          status: CampaignStatus.ARCHIVED
        });
      }
      closeDrawer();
    },
    onError: () => {
      showToast({
        type: ToastType.Error,
        title: 'Something went wrong',
        message: (
          <Typography variant="body2">
            Try stopping the campaign again.{' '}
            <Link
              href="mailto:support@joinpopp.com?subject=Failed to stop campaign"
              target="_blank"
            >
              Contact support
            </Link>{' '}
            if the issue persists.
          </Typography>
        )
      });
    }
  });

  const handleSubmit = async (data: StopCampaignFormData) => {
    const isSending = data.sendCancellationMessage === 'yes';
    await stopCampaign({
      variables: {
        input: {
          campaignId: campaign.id,
          sendCancellationMessage: isSending,
          ...(isSending &&
            data.cancellationMessage && {
              cancellationMessage: data.cancellationMessage
            })
        }
      }
    });
  };

  return (
    <Stack direction="column" gap="24px">
      <Stack component="ul" sx={bulletListSx} color="customGrey.dark">
        <li>Closes all active conversations</li>
        <li>Archives the campaign immediately</li>
        <li>
          Applicants from linked analyses will no longer receive this campaign
        </li>
        <li>Scheduled meetings will not be cancelled</li>
      </Stack>

      <Form
        ref={formRef}
        schema={stopCampaignFormSchema}
        fields={stopCampaignFields}
        defaultValues={{
          sendCancellationMessage: 'no',
          cancellationMessage: ''
        }}
        submitButtonProps={{
          label: 'Stop campaign now',
          sx: stopButtonSx,
          loading,
          disabled: loading
        }}
        cancelButtonProps={{ label: 'Cancel' }}
        onSubmit={handleSubmit}
        onCancel={closeDrawer}
      />
    </Stack>
  );
};
